import { createFileRoute } from "@tanstack/react-router";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Globe2,
  ShieldCheck,
  Truck,
  Package,
  HelpCircle,
} from "lucide-react";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { ContactForm } from "@/components/contact/ContactForm";
import { BusinessHoursBadge } from "@/components/contact/BusinessHoursBadge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import contactHero from "@/assets/contact-hero.jpg";

const CANONICAL = "https://fixhen.com/contact";
const ADDRESS = "6 Prof. Dr Dimitar Dobrev St., Sofia, Bulgaria";
const PHONE = "+359 88 775 2171";
const PHONE_TEL = "+359887752171";
const EMAIL = "info@contact.fixhen.com";
// Approximate lat/lng for the Sofia address — used for the OSM iframe.
// Zoomed-out view centered on Sofia so neighbouring countries are visible.
const OSM_BBOX = "15.5%2C38.5%2C31.0%2C46.5";
const OSM_MARKER = "42.6977%2C23.3219";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact FIXHEN — Sofia, Bulgaria" },
      {
        name: "description",
        content:
          "Reach the FIXHEN team in Sofia, Bulgaria. Email info@contact.fixhen.com, call +359 88 775 2171, or send us a message — Mon–Fri, 09:00–18:00 EET.",
      },
      { property: "og:title", content: "Contact FIXHEN" },
      {
        property: "og:description",
        content: "Talk to a real enthusiast — Mon–Fri, 09:00–18:00 Europe/Sofia.",
      },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `https://fixhen.com${contactHero}` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: `https://fixhen.com${contactHero}` },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "ContactPage",
              url: CANONICAL,
              name: "Contact FIXHEN",
            },
            {
              "@type": "LocalBusiness",
              name: "FIXHEN",
              url: "https://fixhen.com/",
              email: EMAIL,
              telephone: PHONE,
              priceRange: "€€",
              areaServed: "Worldwide",
              address: {
                "@type": "PostalAddress",
                streetAddress: "6 Prof. Dr Dimitar Dobrev St.",
                addressLocality: "Sofia",
                addressCountry: "BG",
              },
              openingHoursSpecification: [
                {
                  "@type": "OpeningHoursSpecification",
                  dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                  opens: "09:00",
                  closes: "18:00",
                },
              ],
              contactPoint: [{
                "@type": "ContactPoint",
                telephone: PHONE,
                email: EMAIL,
                contactType: "customer support",
                areaServed: "Worldwide",
                availableLanguage: ["English", "Bulgarian"],
              }],
            },
            {
              "@type": "FAQPage",
              mainEntity: [
                {
                  "@type": "Question",
                  name: "How fast do you ship?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "In-stock orders leave our Sofia workshop within 1 working day. EU delivery is typically 2–5 days; worldwide 5–12 days with tracking.",
                  },
                },
                {
                  "@type": "Question",
                  name: "What if the part doesn't fit?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Every part is cross-referenced by OEM number and test-fitted on a reference car. If we miss, we cover the return and send the correct part — no questions.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Do you offer wholesale?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Yes — for restoration shops and independent specialists. Send your VAT number and typical monthly volume to info@contact.fixhen.com and we'll set you up with trade pricing.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Can you source a part that isn't in the catalogue?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Often, yes. Send us the chassis number and OEM reference — we work with a network of European suppliers and small-batch manufacturers.",
                  },
                },
              ],
            },
          ],
        }),
      },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  return (
    <div className="flex min-h-screen flex-col bg-background text-ink">
      <AnnouncementBar />
      <SiteHeader />

      <main>
        <Hero />
        <Body />
        <FAQ />
      </main>

      <SiteFooter />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-border bg-secondary/40">
      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-12 sm:px-6 md:grid-cols-[1.4fr_1fr] md:items-end md:py-16">
        <div>
          <BusinessHoursBadge />
          <h1 className="mt-4 text-4xl font-black tracking-tight text-ink sm:text-5xl md:text-6xl">
            We're here to help.
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-ink-soft sm:text-lg">
            Whether it's a fitment question, a wholesale enquiry, or a hard-to-find OEM number —
            drop us a line and a real person will reply within one working day.
          </p>
        </div>
        <div className="hidden md:block">
          <div className="overflow-hidden rounded-2xl border border-border shadow-lg">
            <img
              src={contactHero}
              alt="FIXHEN workshop desk"
              width={1600}
              height={1000}
              loading="lazy"
              className="h-56 w-full object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function Body() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-20">
      <div className="grid gap-10 md:grid-cols-[1.4fr_1fr]">
        <div>
          <h2 className="text-2xl font-bold text-ink">Send us a message</h2>
          <p className="mt-1 text-sm text-ink-soft">
            The more detail the better — chassis number, OEM part number, or a photo helps us route
            your enquiry faster.
          </p>
          <div className="mt-6">
            <ContactForm />
          </div>
        </div>

        <aside className="space-y-6">
          <div className="rounded-2xl border border-border bg-background p-6 shadow-sm">
            <h3 className="text-lg font-bold text-ink">Reach us directly</h3>
            <ul className="mt-4 space-y-4 text-sm">
              <InfoRow icon={Mail} label="Email" value={EMAIL} href={`mailto:${EMAIL}`} />
              <InfoRow icon={Phone} label="Phone" value={PHONE} href={`tel:${PHONE_TEL}`} />
              <InfoRow
                icon={MapPin}
                label="Workshop"
                value={ADDRESS}
                href={`https://www.openstreetmap.org/?mlat=42.6591&mlon=23.3283#map=17/42.6591/23.3283`}
              />
              <li className="flex gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-secondary text-ink">
                  <Clock className="h-4 w-4" />
                </span>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-ink-soft">Hours</div>
                  <div className="text-sm text-ink">Mon – Fri · 09:00 – 18:00</div>
                  <div className="text-xs text-muted-foreground">Europe/Sofia (EET / EEST)</div>
                </div>
              </li>
            </ul>
          </div>

          <div className="overflow-hidden rounded-2xl border border-border bg-background shadow-sm">
            <iframe
              title="FIXHEN workshop location"
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${OSM_BBOX}&layer=mapnik&marker=${OSM_MARKER}`}
              loading="lazy"
              className="h-64 w-full border-0"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div className="flex items-center justify-between border-t border-border px-4 py-2 text-xs">
              <span className="text-muted-foreground">Sofia, Bulgaria</span>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(ADDRESS)}`}
                target="_blank"
                rel="noreferrer noopener"
                className="font-semibold text-ink hover:underline"
              >
                Get directions →
              </a>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-ink p-6 text-paper">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-royal">
              <Globe2 className="h-4 w-4" /> Worldwide shipping
            </div>
            <p className="mt-3 text-sm leading-relaxed text-paper/80">
              We dispatch daily to 150+ countries. Free EU delivery over €99, and every parcel is
              tracked, insured, and packed for the road.
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
}

function InfoRow({
  icon: Icon,
  label,
  value,
  href,
}: {
  icon: typeof Mail;
  label: string;
  value: string;
  href: string;
}) {
  return (
    <li className="flex gap-3">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-secondary text-ink">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase tracking-wider text-ink-soft">{label}</div>
        <a
          href={href}
          target={href.startsWith("http") ? "_blank" : undefined}
          rel={href.startsWith("http") ? "noreferrer noopener" : undefined}
          className="block truncate text-sm text-ink hover:underline"
        >
          {value}
        </a>
      </div>
    </li>
  );
}

function FAQ() {
  const items = [
    {
      icon: Truck,
      q: "How fast do you ship?",
      a: "In-stock orders leave our Sofia workshop within 1 working day. EU delivery is typically 2–5 days; worldwide 5–12 days with tracking.",
    },
    {
      icon: Package,
      q: "What if the part doesn't fit?",
      a: "Every part is cross-referenced by OEM number and test-fitted on a reference car. If we miss, we cover the return and send the correct part — no questions.",
    },
    {
      icon: ShieldCheck,
      q: "Do you offer wholesale?",
      a: "Yes — for restoration shops and independent specialists. Send your VAT number and typical monthly volume to info@contact.fixhen.com and we'll set you up with trade pricing.",
    },
    {
      icon: HelpCircle,
      q: "Can you source a part that isn't in the catalogue?",
      a: "Often, yes. Send us the chassis number and OEM reference — we work with a network of European suppliers and small-batch manufacturers.",
    },
  ];
  return (
    <section className="border-t border-border bg-secondary/40">
      <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6 md:py-20">
        <div className="mb-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-royal">FAQ</p>
          <h2 className="mt-2 text-3xl font-black tracking-tight text-ink sm:text-4xl">
            Before you write in…
          </h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {items.map((it, i) => (
            <AccordionItem
              key={it.q}
              value={`item-${i}`}
              className="rounded-2xl border border-border bg-background px-5"
            >
              <AccordionTrigger className="py-4 text-left text-base font-semibold text-ink hover:no-underline">
                <span className="flex items-center gap-3">
                  <it.icon className="h-5 w-5 text-royal" />
                  {it.q}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-5 pl-8 text-sm leading-relaxed text-ink-soft">
                {it.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}